/**
 * Messages for every signed-in role. Who may talk to whom is decided by the server (a conversation exists only
 * because of a batch, a connection, a shortlist, an approved project or an introduction); this screen shows
 * the conversations the server lists and polls for new messages every few seconds.
 */
import { api } from '../../core/api.js';
import { REPORT_REASONS } from '../../core/constants.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { session } from '../../core/session.js';
import { currentView, every, update } from '../../app/shell.js';
import { av, empty, ic } from '../../ui/components.js';
import { field, selectField } from '../../ui/forms.js';
import { closeModal, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { attachButton, fileChip, uploadChosen } from '../../ui/upload.js';

const POLL_MS = 4000;

const lockedIcon = html`<span class="av" style="background:var(--mgs);color:var(--mg)">${ic('lock')}</span>`;

function list(d, ui) {
  return html`<div class="cl" role="list">${d.conversations.map((c) => html`<div class="ci ${c.id === ui.activeId ? 'on' : ''}" role="listitem" tabindex="0" data-act="msg.open" data-id="${c.id}" data-key="c${c.id}">
    ${c.status === 'locked' ? lockedIcon : av(c.title)}<div class="f1"><div class="l1">${c.title}</div>
    <div class="l2">${c.status === 'locked' ? 'Locked' : c.last_message ? c.last_message.body : 'No messages yet'}</div></div>${c.unread ? html`<span class="tag" aria-label="${c.unread} unread">${c.unread}</span>` : ''}</div>`)}</div>`;
}

function thread(d, ui) {
  const a = ui.active;
  if (!a) return html`<div class="th"><div class="locked"><div>${ic('chat')}<p class="mt-s">Choose a conversation.</p></div></div></div>`;
  const c = a.conversation;
  const locked = c.status === 'locked' || c.status === 'closed';
  return html`<div class="th">
    <div class="thh"><button type="button" class="btn icon m-back" aria-label="Back to chats" data-act="msg.back">${ic('left')}</button>${av(c.title)}
      <div class="f1"><div class="b">${c.title}</div><div class="xs mu">${c.subtitle}</div></div>
      ${locked ? '' : html`<button type="button" class="btn ghost sm" data-act="msg.safety">Safety</button>`}</div>
    ${c.why ? html`<div class="why">${ic('info')}<span>Why this chat exists: ${c.why}</span></div>` : ''}
    ${locked
      ? html`<div class="locked"><div>${ic('lock')}<h3 class="mt-s">This conversation is ${c.status}</h3><p>${c.lock_reason || 'It is not available right now.'}</p></div></div>`
      : html`<div class="msgs" id="msgs-${c.id}" data-stick-bottom>${a.messages.map((m) => (m.kind === 'system'
          ? html`<div class="m sys" data-key="m${m.id}">${m.body}</div>`
          : html`<div class="m ${m.mine ? 'me' : ''}" data-key="m${m.id}"><span class="w">${m.sender_name}</span>${m.body}${fileChip(m.file)}${m.status === 'held' ? html`<div class="xs" style="opacity:.85">Held for review. ${m.hold_reason}</div>` : ''}</div>`))}
        ${c.blocked ? html`<div class="m sys">You blocked this conversation. Unblock from Safety to send messages.</div>` : ''}
        ${c.restricted ? html`<div class="m sys">Restricted: the other person's messages are hidden from you.</div>` : ''}</div>
        <div class="compose">${session.user.role === 'professional' && c.kind === 'batch' && !c.blocked ? attachButton('msg.attach') : ''}<input id="msg-in" placeholder="${c.blocked ? 'Conversation blocked' : 'Write a message'}" ${c.blocked ? 'disabled' : ''} maxlength="2000" aria-label="Message" value="${ui.draft}" data-input="msg.draft" data-enter="msg.send">
          <button type="button" class="btn" data-act="msg.send" ${c.blocked ? 'disabled' : ''} aria-label="Send">${ic('send')}</button></div>`}
  </div>`;
}

async function openConversation(id) {
  const view = currentView();
  const [full] = await Promise.all([api.get(`/messages/conversations/${id}`, { signal: view.ctx.signal })]);
  update((ui, d) => {
    ui.activeId = id;
    ui.thread = true;
    ui.draft = '';
    ui.active = { conversation: full.conversation, messages: full.messages, sinceId: full.messages.at(-1)?.id ?? 0 };
    const row = d.conversations.find((c) => c.id === id);
    if (row) row.unread = 0;
  });
  api.post(`/messages/conversations/${id}/read`).catch(() => {});
}

async function poll() {
  const view = currentView();
  const { conversations } = await api.get('/messages/conversations', { signal: view.ctx.signal });
  const activeId = view.ui.activeId;
  let fresh = null;
  if (activeId) {
    fresh = await api.get(`/messages/conversations/${activeId}`, { signal: view.ctx.signal, query: { since_id: view.ui.active?.sinceId || 0 } });
  }
  update((ui, d) => {
    d.conversations = conversations.map((c) => (c.id === ui.activeId ? { ...c, unread: 0 } : c));
    if (fresh && ui.active && ui.activeId === activeId) {
      ui.active.conversation = fresh.conversation;
      const seen = new Set(ui.active.messages.map((m) => m.id));
      const added = fresh.messages.filter((m) => !seen.has(m.id));
      if (added.length) {
        ui.active.messages.push(...added);
        ui.active.sinceId = ui.active.messages.at(-1).id;
        api.post(`/messages/conversations/${activeId}/read`).catch(() => {});
      }
    }
  });
}

function messagesRoute(role) {
  return {
    path: `/${role}/messages`,
    roles: [role],
    title: 'Messages',
    ui: (ctx) => ({ activeId: null, active: null, thread: Boolean(ctx.query.get('c')), draft: '', want: Number(ctx.query.get('c')) || null }),
    async load(ctx) {
      const [c, rules] = await Promise.all([api.get('/messages/conversations', { signal: ctx.signal }), api.get('/messages/rules', { signal: ctx.signal })]);
      return { conversations: c.conversations, rules };
    },
    async enter() {
      const view = currentView();
      const { conversations } = view.data;
      const wanted = conversations.find((c) => c.id === view.ui.want) || (window.matchMedia('(min-width: 761px)').matches ? conversations[0] : null);
      if (wanted) await openConversation(wanted.id);
      every(POLL_MS, poll);
    },
    view(d, ui) {
      if (!d.conversations.length) {
        return empty('chat', 'No conversations yet', 'Chats open through your batch, connections, shortlists or approved projects.', html`<a class="btn" href="/${role}/${role === 'professional' ? 'network' : role === 'company' ? 'talent' : 'network'}">${role === 'company' ? 'Find talent' : 'Open the network'}</a>`);
      }
      return html`<div class="row mb"><button type="button" class="btn ghost sm" data-act="msg.rules">${ic('info')} Who can I message?</button></div>
        <div class="msgr ${ui.thread ? 'thread-open' : ''}">${list(d, ui)}${thread(d, ui)}</div>
        <p class="note mt-s">Daily limit ${d.rules.per_day} messages. Spam detection applies to every chat.</p>`;
    },
  };
}

register({
  'msg.open': (el) => openConversation(Number(el.dataset.id)),
  'msg.back': () => update((ui) => { ui.thread = false; }),
  'msg.draft': (el) => { currentView().ui.draft = el.value; },
  'msg.attach': async (el) => {
    const view = currentView();
    if (!view.ui.active) return;
    const id = view.ui.activeId;
    try {
      const stored = await uploadChosen(el, 'chat');
      if (!stored) return;
      const input = document.getElementById('msg-in');
      const { message } = await api.post(`/messages/conversations/${id}/messages`, { body: { body: (input?.value || '').trim(), file_id: stored.id } });
      update((ui) => {
        if (!ui.active.messages.some((m) => m.id === message.id)) {
          ui.active.messages.push({ id: message.id, kind: 'user', mine: true, sender_name: 'You', body: message.body, file: message.file, status: message.status, hold_reason: message.hold_reason, at: message.at });
        }
        ui.active.sinceId = Math.max(ui.active.sinceId, message.id);
        ui.draft = '';
      });
      if (input) input.value = '';
      toast('Document shared in the chat', 'check');
    } catch (error) {
      toast(error.message, 'alert');
    } finally {
      el.value = '';
    }
  },
  'msg.send': async () => {
    const view = currentView();
    const input = document.getElementById('msg-in');
    const body = (input?.value || '').trim();
    if (!body || !view.ui.active) return;
    const id = view.ui.activeId;
    try {
      const { message } = await api.post(`/messages/conversations/${id}/messages`, { body: { body } });
      update((ui) => {
        if (!ui.active.messages.some((m) => m.id === message.id)) {
          ui.active.messages.push({ id: message.id, kind: 'user', mine: true, sender_name: 'You', body: message.body, status: message.status, hold_reason: message.hold_reason, at: message.at });
        }
        ui.active.sinceId = Math.max(ui.active.sinceId, message.id);
        ui.draft = '';
      });
      input.value = '';
    } catch (error) {
      if (error.status >= 400 && error.status < 500 && error.status !== 401) toast(error.message, 'alert');
      else throw error;
    }
  },
  'msg.rules': async () => {
    const rules = currentView().data.rules;
    openModal({
      title: 'Messaging is controlled',
      body: html`<p>You can message trainers in your batch, people who accepted your connection request, and organisations that shortlisted you or hired you. To reach someone else, send a connection request from Network.</p>
        <ul>${rules.notes.map((n) => html`<li>${n}</li>`)}</ul><p class="sm mu">You can send up to ${rules.per_day} messages a day and ${rules.connection_requests_per_week} connection requests a week.</p>`,
      foot: html`<button type="button" class="btn" data-act="modal.close">Got it</button>`,
    });
  },
  'msg.safety': () => {
    const c = currentView().ui.active.conversation;
    openModal({
      title: 'Safety for this chat',
      body: html`<p class="mu">These controls are private. The other person is not told who reported them.</p>
        <button type="button" class="opt" data-act="msg.block" data-on="${!c.blocked}" style="width:100%;text-align:left">${ic('ban')}<div><b>${c.blocked ? 'Unblock' : 'Block'}</b><div class="sm mu">${c.blocked ? 'Allow messages again.' : 'Stop all messages in this chat.'}</div></div></button>
        <button type="button" class="opt" data-act="msg.restrict" data-on="${!c.restricted}" style="width:100%;text-align:left">${ic('shield')}<div><b>${c.restricted ? 'Remove restriction' : 'Restrict'}</b><div class="sm mu">${c.restricted ? 'Show their messages again.' : 'Hide their messages without blocking.'}</div></div></button>
        <button type="button" class="opt" data-act="msg.report" style="width:100%;text-align:left">${ic('flag')}<div><b>Report</b><div class="sm mu">Send this conversation to Vignova for review.</div></div></button>`,
    });
  },
  'msg.block': async (el) => {
    const view = currentView();
    const { blocked } = await api.post(`/messages/conversations/${view.ui.activeId}/block`, { body: { blocked: el.dataset.on === 'true' } });
    closeModal(false);
    update((ui) => { ui.active.conversation.blocked = blocked; });
    toast(blocked ? 'Blocked' : 'Unblocked', 'ban');
  },
  'msg.restrict': async (el) => {
    const view = currentView();
    const { restricted } = await api.post(`/messages/conversations/${view.ui.activeId}/restrict`, { body: { restricted: el.dataset.on === 'true' } });
    closeModal(false);
    update((ui) => { ui.active.conversation.restricted = restricted; });
    toast(restricted ? 'Restricted' : 'Restriction removed', 'shield');
  },
  'msg.report': () => openModal({
    title: 'Report conversation',
    body: html`<form id="report-form" data-submit="msg.report.send" novalidate>${selectField('rp-why', 'Reason', REPORT_REASONS, { name: 'reason', val: REPORT_REASONS[0][0] })}
      ${field('rp-details', 'Details', { name: 'details', rows: 3, req: false, max: 2000 })}</form>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="report-form" class="btn danger">Send report</button>`,
  }),
  'msg.report.send': (form) => submitForm(form, async (values) => {
    await api.post(`/messages/conversations/${currentView().ui.activeId}/report`, { body: values });
    closeModal(false);
    toast('Report sent to Vignova. Thank you.', 'flag');
  }),
});

export const routes = ['student', 'professional', 'company', 'startup'].map(messagesRoute);
